import { Check, Loader2, PauseCircle, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { trpc } from '../../lib/trpc';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import type { ProductKey } from './SubscriptionBillingPanel';

type SeatStatus = 'active' | 'paused' | 'none';

interface ProductSeatToggleProps {
  userId: number;
  seatStatus: SeatStatus;
  productKey: ProductKey;
  productLabel: string;
}

/**
 * Per-member "has a seat on {product}" toggle, meant to sit inline in an existing member
 * row (next to the role dropdown/remove button) — seats are per-product, so each portal's
 * Settings page renders one of these per member for its own product only.
 *
 * Rendered on every tier: the Free plan includes real seats, and a seat is what grants product
 * access, so this is the control that decides who can actually use the product.
 *
 * `seatStatus: 'paused'` means the row holds a seat but it's over the plan's current cap (see
 * `sortSeatsByPrecedence` in the backend) — the member has no access right now even though a seat
 * is assigned. Unassigning still works and simply frees the slot for the next-oldest paused seat.
 */
export function ProductSeatToggle({
  userId,
  seatStatus,
  productKey,
  productLabel,
}: ProductSeatToggleProps) {
  const utils = trpc.useUtils();
  const hasSeat = seatStatus !== 'none';

  const invalidate = () => {
    utils.workspace.listProductSeats.invalidate({ productKey });
    utils.workspace.getBillingState.invalidate({ productKey });
  };

  const startTrialMutation = trpc.workspace.startExpansionTrial.useMutation({
    onSuccess: (data) => {
      invalidate();
      toast.success(`Expansion trial started — up to ${data.seats} seats for 14 days.`, {
        description: 'Assign the seat again to add your teammate.',
      });
    },
    onError: (error) => toast.error(error.message || 'Could not start the Team Trial.'),
  });

  const assignMutation = trpc.workspace.assignProductSeat.useMutation({
    onSuccess: invalidate,
    onError: (error) => {
      // The server signals "you have hit the free ceiling but the trial is still unused" rather
      // than starting the clock itself, so the offer stays a deliberate choice.
      if (error.message?.startsWith('TRIAL_AVAILABLE')) {
        toast.info(`The Free plan includes 2 ${productLabel} seats.`, {
          description: 'Start a 14-day Team Trial to use up to 5 seats. No card required.',
          action: {
            label: 'Start trial',
            onClick: () => startTrialMutation.mutate({ productKey }),
          },
          duration: 10000,
        });
        return;
      }
      toast.error(error.message || `Failed to assign ${productLabel} seat.`);
    },
  });

  const unassignMutation = trpc.workspace.unassignProductSeat.useMutation({
    onSuccess: invalidate,
    onError: (error) => toast.error(error.message || `Failed to remove ${productLabel} seat.`),
  });

  const isPending =
    assignMutation.isPending || unassignMutation.isPending || startTrialMutation.isPending;

  return (
    <div className="flex items-center gap-1.5">
      {seatStatus === 'paused' && (
        <Badge
          variant="destructive"
          className="h-7 gap-1 rounded-lg text-[10px] px-1.5 uppercase tracking-wide"
        >
          <PauseCircle className="h-3 w-3" />
          Paused
        </Badge>
      )}
      <Button
        variant={hasSeat ? 'secondary' : 'outline'}
        size="sm"
        className="h-7 rounded-lg text-[11px] px-2"
        disabled={isPending}
        onClick={() =>
          hasSeat
            ? unassignMutation.mutate({ userId, productKey })
            : assignMutation.mutate({ userId, productKey })
        }
      >
        {isPending ? (
          <Loader2 className="h-3 w-3 animate-spin mr-1" />
        ) : hasSeat ? (
          <Check className="h-3 w-3 mr-1" />
        ) : (
          <Plus className="h-3 w-3 mr-1" />
        )}
        {hasSeat ? `${productLabel} Seat` : `Add ${productLabel} Seat`}
      </Button>
    </div>
  );
}
